import { tWithKeyAttr as t } from "../../../i18nHelper";
import React, { useState } from "react";
import styles from "./groupStatusTable.module.css";
import { TQuestionData } from "../../../services/types/user.types";
import HorizontalGraph from "../horizontalGraph/horizontalGraph";
import i18n from "../../../i18n";

type GroupOptionDetail = {
  desc: string;
  answersCount: number;
};

type GroupRow = {
  groupName: string;
  percentages: number[];
  details: GroupOptionDetail[];
};

type Props = {
  question: TQuestionData;
  totalClients: number;
};

export const GroupStatusTable: React.FC<Props> = ({ question, totalClients }) => {
  const [selectedGroup, setSelectedGroup] = useState<GroupRow | null>(null);

  if (!question.options || !question.options.length) return null;

  // If this is an emotion question, use HorizontalGraph instead
  if (question.isEmotion) {
    return <HorizontalGraph question={question} totalClients={totalClients} />;
  }

  const groupsMap = new Map<number, GroupRow>();

  question.options.forEach((option) => {
    if (!option.group || (option.emotion?.emotionId ?? 0) > 0) return;

    const groupId = option.group.groupId;

    if (!groupsMap.has(groupId)) {
      groupsMap.set(groupId, {
        groupName: option.group.groupName,
        percentages: [],
        details: [],
      });
    }

    const group = groupsMap.get(groupId)!;

    if (option.answersCount > 0) {
      group.percentages.push(option.answerPercentage);
    }

    group.details.push({
      desc: option.desc,
      answersCount: option.answersCount,
    });
  });

  const groupRows = Array.from(groupsMap.values());

  return (
    <div dir={i18n.dir()} className={styles.scrollWrapper}>
      <div className={styles.table}>
        {groupRows.map((group, idx) => {
          const validPercents = group.percentages;
          const hasData =
            validPercents.length > 0 && validPercents.some((p) => p > 0);
          const avg = hasData
            ? Math.round(
                validPercents.reduce((a, b) => a + b, 0) / validPercents.length
              )
            : 0;

          return (
            <div
              className={styles.row}
              key={idx}
              onClick={() => setSelectedGroup(group)}
              style={{ cursor: "pointer" }}
            >
              <div className={styles.cell}>{group.groupName}</div>
              <div
                className={`${styles.cell} ${
                  !hasData ? styles.statusNo : styles.statusInactive
                } ${hasData ? styles.inactive : ""}`}
              >
                No
              </div>
              <div
                className={`${styles.cell} ${
                  hasData ? styles.statusYes : styles.statusInactive
                } ${!hasData ? styles.inactive : ""}`}
              >
                {hasData ? `${avg}%` : "Yes"}
              </div>
            </div>
          );
        })}
      </div>

      {selectedGroup && (
        <div
          className={styles.popupOverlay}
          onClick={() => setSelectedGroup(null)}
        >
          <div
            className={styles.popupContent}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.popupContentInner}>
              <div className={styles.popupHeader}>
                <span>{selectedGroup.groupName}</span>
                <button onClick={() => setSelectedGroup(null)}>×</button>
              </div>

              <div className={styles.popupBody}>
                <div className={styles.popupCard}>
                  <table className={styles.popupTable}>
                    <thead>
                      <tr>
                        <th>{t('DESCRIPTION')}</th>
                        <th>{t('NUMBER_OF_ANSWERS')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedGroup.details.map((detail, i) => (
                        <tr key={i}>
                          <td>{detail.desc}</td>
                          <td>{detail.answersCount}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
